import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { clearSession, getAccessToken, getSessionGeneration, hasSessionHint, setAccessToken } from "./authSession";
import { IRefreshTokenResponse } from "../types";

const $api = axios.create({
  withCredentials: true,
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
});
const sessionApi = axios.create({ withCredentials: true, baseURL: process.env.REACT_APP_API_URL, timeout: 15000 });
let refreshPromise: Promise<AxiosResponse<IRefreshTokenResponse>> | null = null;

export const refreshSession = (): Promise<AxiosResponse<IRefreshTokenResponse>> => {
  if (!refreshPromise) {
    const generation = getSessionGeneration();
    refreshPromise = sessionApi
      .get<IRefreshTokenResponse>("/user/refresh")
      .then((response) => {
        if (generation !== getSessionGeneration()) throw new Error("Session ended");
        setAccessToken(response.data.accessToken);
        return response;
      })
      .catch((error) => {
        if (generation === getSessionGeneration() && error.response?.status === 401) clearSession();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

$api.interceptors.request.use(async (config: AxiosRequestConfig) => {
  if (config.headers === undefined) config.headers = {};
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  else delete config.headers.Authorization;
  return config;
});

$api.interceptors.response.use(
  async (config: AxiosResponse) => {
    return config;
  },
  async (error) => {
    const originalRequest = error.config;
    const isSessionRequest = /\/user\/(signin|signup|refresh|logout)/.test(originalRequest?.url || "");
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._isRetry &&
      !isSessionRequest &&
      hasSessionHint()
    ) {
      originalRequest._isRetry = true;
      try {
        await refreshSession();
        return $api.request(originalRequest);
      } catch {}
    }
    return Promise.reject(error);
  }
);

export default $api;
